import React, { useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SideNavbar from '../../../components/SideNavbar';
import StaffModule from '../../../components/CallCRM/staffTopModule';
import OutboundCalls from '../../../components/CallCRM/OutboundCalls';
import TotalCallDuration from '../../../components/CallCRM/TotalCallDuration';
import ConnectionRatio from '../../../components/CallCRM/ConnectionRatio';
import LeadAnalytics from '../../../components/CallCRM/LeadAnalytics';
import StageTimelineAnalytics from '../../../components/CallCRM/LeadStageAnalytics';
import AdmissionAnalytics from '../../../components/CallCRM/AdmissionAnalytics';
import CampaignDistribution from '../../../components/CallCRM/CampaignDistribution';
import ChannelAnalytics from '../../../components/CallCRM/ChannelAnalytics';
import BillingAnalytics from '../../../components/CallCRM/BillingAnalytics';
import ConversionRatio from "../../../components/ConversionRatio";
import CallDistribution from "../../../components/CallDistribution";

// header section 2
import FollowUpCount2 from './dashbord/FollowUpCount';
import TotalTaskCount from './dashbord/TotalTaskCount';
import OutboundCalls2 from './dashbord/OutboundCalls';

// Firebase
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    documentId
} from 'firebase/firestore';
import { db, auth } from "../../../firebaseConfig";

// global
import { useAuth } from '../../../AuthContext';

// function fetchCallData
import { fetchCallMetrics, fetchCallLogsForExport } from '../../../components/CallCRM/Function';
import { exportCallDataToExcel, generateExportFileName } from '../../../utils/excelExport';
import getCurrentScreen from '../../../Helper/GetScreen';
import Toast from '../../../components/Toast';
import ExcelPreviewModal from '../../../components/modals/ExcelPreviewModal';

// redux
import { useAppSelector } from '../../../redux/store';
import FollowUpCount from './dashbord/FollowUpCount';

interface UserOption {
    id: string;
    name: string;
    department?: string; // Add department field
    departmentId?: string; // Add departmentId field
}

interface DepartmentOption {
    id: string;
    name: string;
}

const CallCRMDashboard = () => {
    // Auth context
    const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
    // Redux state
    const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const [showToast, setShowToast] = React.useState(false);
    const [toastMessage, setToastMessage] = React.useState("");
    const [nodata, setNodata] = React.useState(false);
    const [isExporting, setIsExporting] = React.useState(false);
    
    // Preview modal state
    const [showPreviewModal, setShowPreviewModal] = React.useState(false);
    const [previewData, setPreviewData] = React.useState<any[]>([]);
    const [previewFileName, setPreviewFileName] = React.useState("");
    const [isFetchingPreview, setIsFetchingPreview] = React.useState(false);

    // Component state for Call CRM
    const [users, setUsers] = React.useState<UserOption[]>([]);
    const [selectedUser, setSelectedUser] = React.useState<UserOption[]>([]);
    const [searchQuery, setSearchQuery] = React.useState('');

    // Component state for Staff Analytics (separate from Call CRM)
    const [staffAnalyticsSelectedUser, setStaffAnalyticsSelectedUser] = React.useState<UserOption[]>([]);
    const [staffAnalyticsSelectedDepartmentId, setStaffAnalyticsSelectedDepartmentId] = React.useState<string>('all');

    // Date picker state for Call CRM
    const [customDatePopupOpen, setCustomDatePopupOpen] = React.useState(false);
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [endDate, setEndDate] = React.useState<Date | null>(null);
    const [filter, setFilter] = React.useState<boolean>(false);
    const customDatePopupRef = useRef<HTMLDivElement>(null);

    // Separate date picker state for Staff Analytics (OutboundCalls2)
    const [outboundCallsDatePopupOpen, setOutboundCallsDatePopupOpen] = React.useState(false);
    const [outboundCallsStartDate, setOutboundCallsStartDate] = React.useState<Date | null>(new Date());
    const [outboundCallsEndDate, setOutboundCallsEndDate] = React.useState<Date | null>(new Date());
    const outboundCallsDatePopupRef = useRef<HTMLDivElement>(null);

    // Department and user selection (shared across sections, but separate state for Call CRM vs Staff Analytics)
    const [departments, setDepartments] = React.useState<DepartmentOption[]>([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = React.useState<string>('all');

    // Settings state
    interface SettingOption {
        id: string;
        [key: string]: any;
    }

    const [settings, setSettings] = React.useState<{
        stages: SettingOption[];
        campaigns: SettingOption[];
        sources: SettingOption[];
        statuses: SettingOption[];
        admissionStatus: SettingOption[];
    }>({
        stages: [],
        campaigns: [],
        sources: [],
        statuses: [],
        admissionStatus: []
    });

    // ADD: Compute filtered users based on department selection for Call CRM
    const filteredUsersByDepartment = React.useMemo(() => {
        if (selectedDepartmentId === 'all') {
            return users;
        }
        return users.filter(user => user.departmentId === selectedDepartmentId);
    }, [users, selectedDepartmentId]);

    // ADD: Compute filtered users based on department selection for Staff Analytics
    const staffAnalyticsFilteredUsersByDepartment = React.useMemo(() => {
        if (staffAnalyticsSelectedDepartmentId === 'all') {
            return users;
        }
        return users.filter(user => user.departmentId === staffAnalyticsSelectedDepartmentId);
    }, [users, staffAnalyticsSelectedDepartmentId]);

    // Only show departments that have staff
    const departmentsWithStaff = React.useMemo(() => {
        const departmentIdsWithStaff = new Set(users.map(user => user.departmentId));
        return departments.filter(dept => departmentIdsWithStaff.has(dept.id));
    }, [departments, users]);

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (customDatePopupRef.current && !customDatePopupRef.current.contains(event.target as Node)) {
                setCustomDatePopupOpen(false);
            }
            if (outboundCallsDatePopupRef.current && !outboundCallsDatePopupRef.current.contains(event.target as Node)) {
                setOutboundCallsDatePopupOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // inital fetch of all the settings data from the database
    useEffect(() => {
        if (!userData?.companyId) return;
        const stageRef = collection(db, `companies/${userData?.companyId}/leadStages`);
        const campaignRef = collection(db, `companies/${userData?.companyId}/leadCampaigns`);
        const sourceRef = collection(db, `companies/${userData?.companyId}/leadSources`);
        const statusRef = collection(db, `companies/${userData?.companyId}/leadStatuses`);
        const admissionStatusRef = collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`);
        const fetchSettings = async () => {
            try {
                const stageSnapshot = await getDocs(stageRef);
                const campaignSnapshot = await getDocs(campaignRef);
                const sourceSnapshot = await getDocs(sourceRef);
                const statusSnapshot = await getDocs(statusRef);
                const admissionStatusSnapshot = await getDocs(admissionStatusRef);
                const stages = stageSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                const campaigns = campaignSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                const sources = sourceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                const statuses = statusSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                const admissionStatus = admissionStatusSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setSettings({ stages, campaigns, sources, statuses, admissionStatus });
            } catch (error) {
                console.error("Error fetching settings:", error);
            }
        };

        fetchSettings();
    }, [userData?.companyId]);

    const fetchDepartments = React.useCallback(async () => {
        if (!userData?.companyId) return;

        try {
            const companyDepartmentsSnapshot = await getDocs(
                collection(db, `companies/${userData.companyId}/departments`)
            );
            setDepartments(
                companyDepartmentsSnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        name: doc.data().department
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name))
            );
        } catch (error) {
            console.error("Error fetching departments:", error);
        }
    }, [userData?.companyId]);

    const fetchAssignedToOptions = React.useCallback(async () => {
        if (!userData?.companyId) return;

        try {
            // CHANGE: Remove department filter from the query - fetch all users first
            let usersQuery;

            // Rest of your role-based filtering...
            if (isManager && currentScreen === 'Manager') {
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", userData?.companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );
            } else if (isStaff && currentScreen === 'Staff') {
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", userData?.companyId),
                    where(documentId(), "==", currentUser?.uid)
                );
            } else {
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", userData?.companyId)
                );
            }

            const usersSnapshot = await getDocs(usersQuery);
            const allUsers = usersSnapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().name,
                department: doc.data().department, // Include department name for display
                departmentId: doc.data().departmentId // Include departmentId for filtering
            }));

            if (allUsers.length === 0) {
                setToastMessage("No users found in your team");
                setShowToast(true);
                setNodata(true);
                setUsers([]);
                setSelectedUser([]);
            } else {
                setUsers(allUsers);
                setNodata(false);
                // CHANGE: Update selected users based on department filter
                if (selectedDepartmentId !== 'all') {
                    const departmentUsers = allUsers.filter(user => user.departmentId === selectedDepartmentId);
                    setSelectedUser(departmentUsers);
                } else {
                    // When "All Departments" is selected, for Manager screen include all users they manage
                    if (currentScreen === 'Manager') {
                        setSelectedUser(allUsers);
                    } else if (currentScreen === 'Staff') {
                        // For Staff screen, always include the staff member themselves
                        setSelectedUser(allUsers);
                    } else {
                        setSelectedUser([]);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    }, [userData?.companyId, isManager, isStaff, currentScreen, currentUser?.uid]);


    React.useEffect(() => {
        fetchDepartments();
        fetchAssignedToOptions();
    }, [fetchAssignedToOptions, fetchDepartments]);

    // CHANGE: Update selected users when department changes for Call CRM
    React.useEffect(() => {
        if (selectedDepartmentId !== 'all') {
            const departmentUsers = users.filter(user => user.departmentId === selectedDepartmentId);
            setSelectedUser(departmentUsers);
        } else {
            // When "All Departments" is selected, for Manager screen include all users they manage
            if (currentScreen === 'Manager') {
                setSelectedUser(users); // Include all users that manager manages
            } else if (currentScreen === 'Staff') {
                // For Staff screen, always include the staff member themselves
                setSelectedUser(users);
            } else {
                setSelectedUser([]);
            }
        }
    }, [selectedDepartmentId, users, currentScreen]);

    // CHANGE: Update selected users when department changes for Staff Analytics
    React.useEffect(() => {
        if (staffAnalyticsSelectedDepartmentId !== 'all') {
            const departmentUsers = users.filter(user => user.departmentId === staffAnalyticsSelectedDepartmentId);
            setStaffAnalyticsSelectedUser(departmentUsers);
        } else {
            // When "All Departments" is selected, for Manager screen include all users they manage
            if (currentScreen === 'Manager') {
                setStaffAnalyticsSelectedUser(users); // Include all users that manager manages
            } else if (currentScreen === 'Staff') {
                // For Staff screen, always include the staff member themselves
                setStaffAnalyticsSelectedUser(users);
            } else {
                setStaffAnalyticsSelectedUser([]);
            }
        }
    }, [staffAnalyticsSelectedDepartmentId, users, currentScreen]);

    // CHANGE: Filter users for dropdown based on department for Call CRM
    const filteredUsers = React.useMemo(() => {
        let baseUsers = users;

        // Apply department filter first
        if (selectedDepartmentId !== 'all') {
            baseUsers = users.filter(user => user.departmentId === selectedDepartmentId);
        }

        // Then apply search filter
        return baseUsers.filter(user =>
            user.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [users, selectedDepartmentId, searchQuery]);

    // CHANGE: Filter users for dropdown based on department for Staff Analytics
    const staffAnalyticsFilteredUsers = React.useMemo(() => {
        let baseUsers = users;

        // Apply department filter first
        if (staffAnalyticsSelectedDepartmentId !== 'all') {
            baseUsers = users.filter(user => user.departmentId === staffAnalyticsSelectedDepartmentId);
        }

        // Then apply search filter
        return baseUsers.filter(user =>
            user.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [users, staffAnalyticsSelectedDepartmentId, searchQuery]);

    const handleSelectedUser = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (e.target.value === 'All Staff') {
            // If department is selected, include all users from that department
            if (selectedDepartmentId !== 'all') {
                const departmentUsers = users.filter(user => user.departmentId === selectedDepartmentId);
                setSelectedUser(departmentUsers);
            } else {
                // When "All Departments" is selected, for Manager screen include all users they manage
                if (currentScreen === 'Manager') {
                    setSelectedUser(users); // Include all users that manager manages
                } else if (currentScreen === 'Staff') {
                    // For Staff screen, always include the staff member themselves
                    setSelectedUser(users);
                } else {
                    setSelectedUser([]);
                }
            }
            return;
        }
        // Single user selection
        setSelectedUser([{ id: e.target.value, name: '', departmentId: '', department: '' }]);
    };

    // Handler for Staff Analytics user selection
    const handleStaffAnalyticsSelectedUser = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (e.target.value === 'All Staff') {
            // If department is selected, include all users from that department
            if (staffAnalyticsSelectedDepartmentId !== 'all') {
                const departmentUsers = users.filter(user => user.departmentId === staffAnalyticsSelectedDepartmentId);
                setStaffAnalyticsSelectedUser(departmentUsers);
            } else {
                // When "All Departments" is selected, for Manager screen include all users they manage
                if (currentScreen === 'Manager') {
                    setStaffAnalyticsSelectedUser(users); // Include all users that manager manages
                } else if (currentScreen === 'Staff') {
                    // For Staff screen, always include the staff member themselves
                    setStaffAnalyticsSelectedUser(users);
                } else {
                    setStaffAnalyticsSelectedUser([]);
                }
            }
            return;
        }
        // Single user selection
        setStaffAnalyticsSelectedUser([{ id: e.target.value, name: '', departmentId: '', department: '' }]);
    };

    // Updated handler for department selection for Call CRM
    const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedDepartmentId(e.target.value);
    };

    // Updated handler for department selection for Staff Analytics
    const handleStaffAnalyticsDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setStaffAnalyticsSelectedDepartmentId(e.target.value);
    };

    const handleDateRangeClick = () => {
        setCustomDatePopupOpen(!customDatePopupOpen);
    };

    // OutboundCalls2 date range functions
    const handleOutboundCallsDateRangeClick = () => {
        setOutboundCallsDatePopupOpen(!outboundCallsDatePopupOpen);
    };

    const handleOutboundCallsDateRange = (date: Date | null, type: string) => {
        if (!date) return null;

        if (type === 'start') {
            date.setHours(5, 30, 0, 0);
        } else {
            // Add one day for end date
            const nextDay = new Date(date);
            nextDay.setDate(nextDay.getDate() + 1);
            nextDay.setHours(5, 29, 59, 0);
            return nextDay.toISOString();
        }

        return date.toISOString();
    };

    const handleOutboundCallsApplyFilters = () => {
        setOutboundCallsDatePopupOpen(false);
    };

    useEffect(() => {
        setStartDate(new Date());
        setEndDate(new Date());
    }, [userData?.companyId, users, currentScreen]);

    const handleDateRange = (date: Date | null, type: string) => {
        if (!date) return null;

        if (type === 'start') {
            date.setHours(5, 30, 0, 0);
        } else {
            // Add one day for end date
            const nextDay = new Date(date);
            nextDay.setDate(nextDay.getDate() + 1);
            nextDay.setHours(5, 29, 59, 0);
            return nextDay.toISOString();
        }

        return date.toISOString();
    };

    const handleApplyFilters = () => {
        setFilter(true);
        setCustomDatePopupOpen(false);
    };

    const handleDownloadExcel = async () => {
        if (!userData?.companyId) {
            setToastMessage('User data not available');
            setShowToast(true);
            return;
        }

        setIsFetchingPreview(true);
        try {
            // CHANGE: Use filteredUsersByDepartment for export
            const callLogs = await fetchCallLogsForExport({
                db,
                userData,
                startTime: handleDateRange(startDate, 'start'),
                endTime: handleDateRange(endDate, 'end'),
                users: filteredUsersByDepartment,
                currentScreen,
                selectedUser,
                currentUserId: currentUser?.uid
            });

            if (callLogs.length === 0) {
                setToastMessage('No staff members found or no call data available for the selected criteria');
                setShowToast(true);
                setIsFetchingPreview(false);
                return;
            }

            // Generate appropriate filename
            const fileName = generateExportFileName(
                currentScreen,
                selectedUser,
                startDate,
                endDate,
                filteredUsersByDepartment
            );

            // Set preview data and show modal
            setPreviewData(callLogs);
            setPreviewFileName(fileName);
            setShowPreviewModal(true);
        } catch (error) {
            console.error('Error fetching call data:', error);
            setToastMessage('Failed to fetch call data. Please try again.');
            setShowToast(true);
        } finally {
            setIsFetchingPreview(false);
        }
    };

    // Handler for successful download from modal
    const handleDownloadSuccess = (message: string) => {
        setToastMessage(message);
        setShowToast(true);
    };

    // Handler for download error from modal
    const handleDownloadError = (message: string) => {
        setToastMessage(message);
        setShowToast(true);
    };

    // Handler to close preview modal
    const handleClosePreviewModal = () => {
        setShowPreviewModal(false);
        setPreviewData([]);
        setPreviewFileName("");
    };

    return (
        <div className="min-h-screen bg-gray-200">
            {/* Left Sidebar */}
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            {/* Main Content */}
            {!nodata ? (
                <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[110px] p-4 lg:p-8 transition-all duration-300`}>

                    {currentScreen === 'Staff' && (
                        <div className='mb-3'>
                            <StaffModule />
                        </div>
                    )}

                    {/* Top Header Section */}
                    <div className="bg-white p-4 lg:p-6 rounded-3xl shadow-md mb-3">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6 gap-4">
                            {/* Left side with icon and title */}
                            <div className="flex items-center gap-4">
                                <div className="flex items-center justify-center w-10 h-10 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)" }}>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/call-dropped.svg`}
                                        alt="Icon" className="w-6 h-6" />
                                </div>
                                <div className="flex items-center">
                                    <h2 className="text-lg lg:text-xl text-black font-medium"
                                        style={{ fontFamily: "Montserrat", lineHeight: "29.26px" }}>
                                        Call CRM
                                    </h2>
                                </div>
                            </div>

                            {/* Right side with staff selection and date range */}
                            <div className="flex items-center gap-4">
                                {currentScreen !== 'Staff' && (
                                    <>
                                        <select
                                            className="w-full sm:w-auto p-2 border border-cyan-600 text-sm rounded-lg bg-white"
                                            value={selectedDepartmentId}
                                            onChange={handleDepartmentChange}
                                        >
                                            <option value="all">All Departments</option>
                                            {departmentsWithStaff.map(dept => (
                                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                                            ))}
                                        </select>

                                        <select
                                            className="w-full sm:w-auto p-2 border border-cyan-600 text-sm rounded-lg bg-white"
                                            value={
                                                selectedUser.length === 0 ? 'All Staff' :
                                                    selectedUser.length === filteredUsers.length && selectedDepartmentId !== 'all' ? 'All Staff' :
                                                        selectedUser.length === 1 ? selectedUser[0].id :
                                                            'Multiple Selected'
                                            }
                                            onChange={handleSelectedUser}
                                        >
                                            <option value="All Staff">
                                                {selectedDepartmentId !== 'all'
                                                    ? `All Staff (${departments.find(d => d.id === selectedDepartmentId)?.name || 'Selected Department'})`
                                                    : 'All Staff'
                                                }
                                            </option>
                                            {filteredUsers.map(user => (
                                                <option key={user.id} value={user.id}>{user.name}</option>
                                            ))}
                                        </select>
                                    </>
                                )}

                                <div className="relative">
                                    <div
                                        className="w-full sm:w-auto py-2 px-1 border border-cyan-600 text-sm rounded-lg bg-white cursor-pointer"
                                        onClick={handleDateRangeClick}
                                    >
                                        {startDate === null && endDate === null
                                            ? "Lifetime"
                                            : startDate && endDate
                                                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                                                : "Select Date Range"}
                                    </div>

                                    {customDatePopupOpen && (
                                        <div
                                            ref={customDatePopupRef}
                                            className="absolute top-full right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
                                        >
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="text-[14px] font-normal leading-[20px] text-left">
                                                    Select Date Range
                                                </h3>
                                            </div>
                                            <div className="mb-1">
                                                <span className="text-[12px] font-normal leading-[16px] text-left">
                                                    <span className='font-medium'>Current: </span>
                                                    {startDate && endDate
                                                        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                                                        : "Select Date Range"}
                                                </span>
                                                <div className="grid grid-cols-2 gap-2 mt-2">
                                                    <button onClick={() => { setStartDate(new Date()); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                                                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 7))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
                                                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 30))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
                                                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 90))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
                                                    {/* Lifetime filter */}
                                                    <button onClick={() => { setStartDate(null); setEndDate(null); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Lifetime</button>
                                                </div>
                                                <div
                                                    className="p-4 bg-white shadow-lg rounded-[12px]"
                                                    style={{ width: '100%', height: 'auto' }}
                                                >
                                                    <DatePicker
                                                        selected={startDate}
                                                        startDate={startDate || undefined}
                                                        endDate={endDate || undefined}
                                                        selectsRange
                                                        onChange={(update) => {
                                                            const [start, end] = update;
                                                            setStartDate(start);
                                                            setEndDate(end);
                                                        }}
                                                        inline
                                                        className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                                                        maxDate={new Date()}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-between mt-2">
                                                <button
                                                    className="px-8 py-2 text-white bg-black rounded-full"
                                                    onClick={() => setCustomDatePopupOpen(false)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                                    onClick={handleApplyFilters}
                                                // disabled={!startDate || !endDate}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Download Excel Button */}
                                <button
                                    onClick={handleDownloadExcel}
                                    disabled={isFetchingPreview}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg border transition-colors ${isFetchingPreview
                                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                                        : 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                                        }`}
                                    title="Preview and download call data as Excel file"
                                >
                                    {isFetchingPreview ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Fetching Preview...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            Preview Excel
                                        </>
                                    )}
                                </button>

                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div className="rounded-lg">
                                <OutboundCalls users={filteredUsersByDepartment} selectedUser={selectedUser} startTime={handleDateRange(startDate, 'start')} endTime={handleDateRange(endDate, 'end')} />
                            </div>
                            <div className="p-4 border border-cyan-600 rounded-lg">
                                <TotalCallDuration users={filteredUsersByDepartment} selectedUser={selectedUser} startTime={handleDateRange(startDate, 'start')} endTime={handleDateRange(endDate, 'end')} />
                            </div>
                            <div className="p-4 border border-cyan-600 rounded-lg">
                                <ConnectionRatio users={filteredUsersByDepartment} selectedUser={selectedUser} startTime={handleDateRange(startDate, 'start')} endTime={handleDateRange(endDate, 'end')} />
                            </div>
                        </div>
                    </div>

                    {/* header section 2 */}
                    <div className="bg-white p-4 lg:p-6 rounded-3xl shadow-md mb-3">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6 gap-4">
                            {/* Left side with icon and title */}
                            <div className="flex items-center gap-4">
                                <div className="flex items-center justify-center w-10 h-10 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)" }}>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/analytics-svg.svg`}
                                        alt="Icon" className="w-6 h-6" />
                                </div>
                                <div className="flex items-center">
                                    <h2 className="text-lg lg:text-xl text-black font-medium"
                                        style={{ fontFamily: "Montserrat", lineHeight: "29.26px" }}>
                                        Staff Analytics <span className="text-m font-medium text-gray-500">(Beta)</span>
                                    </h2>
                                </div>
                            </div>

                            {/* Right side with staff selection and date range */}
                            <div className="flex items-center gap-4">
                                {currentScreen !== 'Staff' && (
                                    <>
                                        <select
                                            className="w-full sm:w-auto p-2 border border-cyan-600 text-sm rounded-lg bg-white"
                                            value={staffAnalyticsSelectedDepartmentId}
                                            onChange={handleStaffAnalyticsDepartmentChange}
                                        >
                                            <option value="all">All Departments</option>
                                            {departmentsWithStaff.map(dept => (
                                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                                            ))}
                                        </select>

                                        <select
                                            className="w-full sm:w-auto p-2 border border-cyan-600 text-sm rounded-lg bg-white"
                                            value={
                                                staffAnalyticsSelectedUser.length === 0 ? 'All Staff' :
                                                    staffAnalyticsSelectedUser.length === staffAnalyticsFilteredUsers.length && staffAnalyticsSelectedDepartmentId !== 'all' ? 'All Staff' :
                                                        staffAnalyticsSelectedUser.length === 1 ? staffAnalyticsSelectedUser[0].id :
                                                            'Multiple Selected'
                                            }
                                            onChange={handleStaffAnalyticsSelectedUser}
                                        >
                                            <option value="All Staff">
                                                {staffAnalyticsSelectedDepartmentId !== 'all'
                                                    ? `All Staff (${departments.find(d => d.id === staffAnalyticsSelectedDepartmentId)?.name || 'Selected Department'})`
                                                    : 'All Staff'
                                                }
                                            </option>
                                            {staffAnalyticsFilteredUsers.map(user => (
                                                <option key={user.id} value={user.id}>{user.name}</option>
                                            ))}
                                        </select>
                                    </>
                                )}

                                <div className="relative">
                                    <div
                                        className="w-full sm:w-auto py-2 px-1 border border-cyan-600 text-sm rounded-lg bg-white cursor-pointer"
                                        onClick={handleOutboundCallsDateRangeClick}
                                    >
                                        {outboundCallsStartDate === null && outboundCallsEndDate === null
                                            ? "Lifetime"
                                            : outboundCallsStartDate && outboundCallsEndDate
                                                ? `${outboundCallsStartDate.toLocaleDateString()} - ${outboundCallsEndDate.toLocaleDateString()}`
                                                : "Select Date Range"}
                                    </div>

                                    {outboundCallsDatePopupOpen && (
                                        <div
                                            ref={outboundCallsDatePopupRef}
                                            className="absolute top-full right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
                                        >
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="text-[14px] font-normal leading-[20px] text-left">
                                                    Select Date Range for OutboundCalls
                                                </h3>
                                            </div>
                                            <div className="mb-1">
                                                <span className="text-[12px] font-normal leading-[16px] text-left">
                                                    <span className='font-medium'>Current: </span>
                                                    {outboundCallsStartDate && outboundCallsEndDate
                                                        ? `${outboundCallsStartDate.toLocaleDateString()} - ${outboundCallsEndDate.toLocaleDateString()}`
                                                        : "Select Date Range"}
                                                </span>
                                                <div className="grid grid-cols-2 gap-2 mt-2">
                                                    <button onClick={() => { setOutboundCallsStartDate(new Date()); setOutboundCallsEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                                                    <button onClick={() => { setOutboundCallsStartDate(new Date(new Date().setDate(new Date().getDate() - 7))); setOutboundCallsEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
                                                    <button onClick={() => { setOutboundCallsStartDate(new Date(new Date().setDate(new Date().getDate() - 30))); setOutboundCallsEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
                                                    <button onClick={() => { setOutboundCallsStartDate(new Date(new Date().setDate(new Date().getDate() - 90))); setOutboundCallsEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
                                                    {/* Lifetime filter */}
                                                    <button onClick={() => { setOutboundCallsStartDate(null); setOutboundCallsEndDate(null); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Lifetime</button>
                                                </div>
                                                <div
                                                    className="p-4 bg-white shadow-lg rounded-[12px]"
                                                    style={{ width: '100%', height: 'auto' }}
                                                >
                                                    <DatePicker
                                                        selected={outboundCallsStartDate}
                                                        startDate={outboundCallsStartDate || undefined}
                                                        endDate={outboundCallsEndDate || undefined}
                                                        selectsRange
                                                        onChange={(update) => {
                                                            const [start, end] = update;
                                                            setOutboundCallsStartDate(start);
                                                            setOutboundCallsEndDate(end);
                                                        }}
                                                        inline
                                                        className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                                                        maxDate={new Date()}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-between mt-2">
                                                <button
                                                    className="px-8 py-2 text-white bg-black rounded-full"
                                                    onClick={() => setOutboundCallsDatePopupOpen(false)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                                    onClick={handleOutboundCallsApplyFilters}
                                                // disabled={!outboundCallsStartDate || !outboundCallsEndDate}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Download Excel Button */}
                                {/* <button
                                    onClick={handleDownloadExcel}
                                    disabled={isExporting}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg border transition-colors ${isExporting
                                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                                        : 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                                        }`}
                                    title="Download call data as Excel file"
                                >
                                    {isExporting ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Download Excel
                                        </>
                                    )}
                                </button> */}

                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div className="rounded-lg">
                                <OutboundCalls2 users={staffAnalyticsFilteredUsersByDepartment} selectedUser={staffAnalyticsSelectedUser} startTime={handleOutboundCallsDateRange(outboundCallsStartDate, 'start')} endTime={handleOutboundCallsDateRange(outboundCallsEndDate, 'end')} />
                            </div>
                            <div className="p-4 border border-cyan-600 rounded-lg">
                                <FollowUpCount2 users={staffAnalyticsFilteredUsersByDepartment} selectedUser={staffAnalyticsSelectedUser} startTime={null} endTime={null} showTooltip={outboundCallsStartDate !== null && outboundCallsEndDate !== null} />
                            </div>
                            <div className="p-4 border border-cyan-600 rounded-lg">
                                <TotalTaskCount users={staffAnalyticsFilteredUsersByDepartment} selectedUser={staffAnalyticsSelectedUser} startTime={null} endTime={null} showTooltip={outboundCallsStartDate !== null && outboundCallsEndDate !== null} />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-3 mb-3">
                        <div className="w-full lg:w-[60%] bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                            <LeadAnalytics settings={settings} />
                        </div>
                        <div className="w-full lg:w-[40%] bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                            <StageTimelineAnalytics settings={settings} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
                        <div className="bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                            <AdmissionAnalytics settings={settings} />
                        </div>
                        {currentScreen !== 'Staff' && (
                            <div className="bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                                <CampaignDistribution users={filteredUsersByDepartment} settings={settings} />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col lg:flex-row gap-3">
                        {currentScreen !== 'Staff' && (
                            <div className="w-full lg:w-[60%] bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                                <ChannelAnalytics users={filteredUsersByDepartment} settings={settings} />
                            </div>
                        )}
                        {currentScreen !== 'Staff' && currentScreen !== 'Manager' && (
                            <div className="w-full lg:w-[40%] bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                                <BillingAnalytics />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[110px] p-4 lg:p-8 transition-all duration-300`}>
                    <div className="bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                        <h2 className="text-lg lg:text-xl text-black font-medium text-center"
                            style={{ fontFamily: "Montserrat", lineHeight: "29.26px" }}>
                            No Data Found
                        </h2>
                    </div>
                </div>
            )}
            {/* Toast message */}
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
            
            {/* Excel Preview Modal */}
            <ExcelPreviewModal
                isOpen={showPreviewModal}
                onClose={handleClosePreviewModal}
                data={previewData}
                fileName={previewFileName}
                onDownloadSuccess={handleDownloadSuccess}
                onDownloadError={handleDownloadError}
            />
        </div>
    );
};

export default CallCRMDashboard;